/* eslint-disable camelcase */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Button, Form } from 'react-bootstrap';
import { intlModule } from '@chedri/base';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { getStripeConfig, useStripeConfig } from 'helpers/payment';
import { getNameInCurrentLanguage } from '../../../helpers/intl';
import {
  applyCartEditionPreviewFromEvent,
  cartUpdateItem,
  refreshCartEditionPreview,
} from '../../../actions/CartActions';
import { REALTIME_EVENT_TYPES, entityRoom } from 'js/constants/realtimeEventTypes';
import useRealtimeEvent from 'js/hooks/useRealtimeEvent';
import Spinner from '../../Spinner';
import CartWizardFinish from './CartWizardFinish';
import CartWizardStep from './CartWizardStep';
import globals from 'js/globals';
import withRouter from 'js/withRouter';
import { legacyInjectIntl } from 'js/wrapper/injectIntl';

const { debug, whiteLabelSettings } = globals;

const EDITION_ENTITY_TYPE = 'EditionBundle\\Document\\Edition';

/* use it if we get isStripeActive and stripeKey via whiteLabelSettings from global. Otherwise use the alternative below.
  const { isActive: isStripeActive, key: stripeKey } = getStripeConfig(
    typeof whiteLabelSettings !== 'undefined' && whiteLabelSettings
  );

  const stripePromise = isStripeActive ? loadStripe(stripeKey) : null;
 */



function projectCart(cart, locale) {
  const { id, items } = cart;
  return {
    id,
    items: items.map(getCartItem(locale, cart)),
  };
}

function getCartItem(locale, cart) {
  const intl = { displayIntlForm: locale };
  const { shipping_address } = cart;
  return cartItem => {
    const {
      id,
      price,
      print_pdf_price,
      gross,
      print_type,
      edition,
      item,
      variant,
      is_configuration_finished,
      campaign_budget,
      campaign_running_budget,
      user_budget,
      user_running_budget,
      coupon,
      coupon_value,
      is_reduced_vat_rate_check_requested,
      is_reduced_vat_rate_check_available,
      is_manual_data_check_requested,
      manual_data_check_price,
      manual_data_check_request_comment,
      is_web_pdf_requested,
      web_pdf_price,
      is_wants_domain_hosting,
      domain_hosting_net_amount,
      desired_domain,
      url_forwarding,
      available_shipping_types,
      shipping_type,
      split_shipping,
      split_shipping_price,
      applied_product_group_discount,
      perforation_options_available,
      perforation_options_selected,
      perforation_options_preselected,
      textile_sizes_option,
      textile_sizes_selected,
    } = cartItem;
    let { name } = item;
    let itemName;
    typeof name === 'object' ? (itemName = getNameInCurrentLanguage(name, intl)) : (itemName = name);
    name = itemName;
    const { title: label, campaign, mdate, cdate } = edition;
    const { currency } = variant ? variant.pricing : item.pricing;
    const { vat_rate: vat } =
      cartItem.print_type === 'pdf_only'
        ? 19
        : !variant
        ? Math.round(item.pricing.gross_price / item.pricing.price)
        : Math.round(variant.pricing.gross_price / variant.pricing.price);
    const variantDetails = variant ? getItemDetails(variant, intl) : [];
    const details = [
      {
        key: 'format',
        label: 'Format',
        description: edition.template.size_text,
      },
      ...getItemDetails(item, intl),
      ...variantDetails,
    ];

    // TODO: use spread syntax
    // only props marked with "diff" comment differs from the input

    return {
      id,
      name, // diff
      label, // diff
      price,
      print_pdf_price,
      gross,
      currency, // diff
      vat, // diff
      lastModified: new Date(mdate), // diff
      lastStored: new Date(cdate), // diff
      details, // diff
      campaign, // diff
      print_type,
      edition,
      item,
      variant,
      is_configuration_finished,
      campaign_budget,
      campaign_running_budget,
      user_budget,
      user_running_budget,
      coupon,
      coupon_value,
      is_reduced_vat_rate_check_requested,
      is_reduced_vat_rate_check_available,
      is_manual_data_check_requested,
      manual_data_check_price,
      manual_data_check_request_comment,
      is_web_pdf_requested,
      web_pdf_price,
      is_wants_domain_hosting,
      domain_hosting_net_amount,
      desired_domain,
      url_forwarding,
      available_shipping_types,
      shipping_type,
      split_shipping,
      split_shipping_price,
      shipping_address, // diff
      applied_product_group_discount,
      perforation_options_available,
      perforation_options_selected,
      perforation_options_preselected,
      textile_sizes_option,
      textile_sizes_selected,
    };
  };
}

function getEditionIdFromHash() {
  const fullHash = window.location.hash;
  if (fullHash && fullHash.length > 1) {
    return fullHash.substring(1);
  }

  return null;
}

function CartWizard({history, appIntl}) {
  const dispatch = useDispatch();
  const { isActive: isStripeActive, key: stripeKey } = useStripeConfig("CartWizard");
  // We use useMemo so that LoadStripe doesn't fire again with every render.
  const stripePromise = React.useMemo(() => {
    if (isStripeActive && stripeKey) {
      return loadStripe(stripeKey);
    }
    return null;
  }, [isStripeActive, stripeKey]);
  const locale = useSelector(intlModule.selectors.getLocale);
  const messages = useSelector(intlModule.selectors.getMessages);
  const originalCart = useSelector(state => state.cart);
  const isCartPending = useSelector(state => state.cart.pending);
  const isAuthPending = useSelector(state => state.auth.loginPending);

  const [cart, setCart] = useState();
  const [editionId, setEditionId] = useState();

  const confirmStep = useCallback(
    cartItem => {
      dispatch(
        cartUpdateItem(cartItem, {
          is_configuration_finished: true,
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    if (originalCart) {
      setCart(projectCart(originalCart, locale));
    }
  }, [originalCart, locale]);

  useEffect(() => {
    setEditionId(getEditionIdFromHash());
  }, []);

  const editionSubscribeRooms = useMemo(
    () =>
      (cart?.items ?? [])
        .filter(cartItem => !cartItem?.edition?.template?.direct_ordering && cartItem?.edition?.id)
        .map(cartItem => entityRoom(EDITION_ENTITY_TYPE, cartItem.edition.id)),
    [cart]
  );

  useRealtimeEvent(
    REALTIME_EVENT_TYPES.EDITION_PREVIEW_UPDATED,
    event => {
      const editionId = event?.scope?.entityId;
      if (!editionId) {
        return;
      }

      if (Array.isArray(event?.payload?.previews) && event.payload.previews.length > 0) {
        dispatch(applyCartEditionPreviewFromEvent(editionId, event.payload));
        return;
      }

      dispatch(refreshCartEditionPreview(editionId));
    },
    {
      subscribeRooms: editionSubscribeRooms,
      enabled: Boolean(cart?.items?.length),
    }
  );

  if ((isCartPending && (!cart || !cart.id)) || isAuthPending) {
    return (
      <div className="full-width text-center top-gap">
        <Spinner />
      </div>
    );
  }

  if (cart && cart.items.length === 0) {
    return <h5 className="text-center top-gap">{messages.cart_is_empty}</h5>;
  }

  let countUnselectedDomainRegistration = 0;
  let countUnselectedDataCheck = 0;
  let countDataCheckSet = 0;

  const backToEditor = (editionId) => {
    const url = (appIntl?.urlPrefix ?? '') + 'editor/' + editionId;
    history.push(url);
  }

  return (
    <div className="wizard top-gap">
      {editionId && (
        <Button
          variant="success"
          size="lg"
          className="ms-3 cart-back-to-editor-button"
          onClick={() => {backToEditor(editionId)}}
        >
          <i className={'fa-solid fa-pencil'} /> {messages.editor_toolbar.back_to_the_editor}
        </Button>
      )}

      <Form>
        {cart &&
          Array.isArray(cart.items) &&
          cart.items.map((cartItem, index) => {
            if (cartItem.item.type === 'itemWebsite') {
              if (!cartItem.is_wants_domain_hosting) {
                countUnselectedDomainRegistration++;
              }
            } else if (!cartItem.is_manual_data_check_requested) {
              countUnselectedDataCheck++;
            }

            if (
              cartItem?.edition?.item?.pricing?.dataproof_base_price > 0 ||
              cartItem?.edition?.item?.pricing?.new_dataproof_base_price > 0
            ) {
              countDataCheckSet++;
            }

            return (
              <CartWizardStep
                key={cartItem.id}
                number={index + 1}
                cartItem={cartItem}
                onConfirm={() => confirmStep(cartItem)}
              />
            );
          })}

        {isStripeActive && stripePromise ? (
          <Elements stripe={stripePromise}>
            <CartWizardFinish
              countMissingDataChecks={countUnselectedDataCheck}
              countMissingDomainRegistrations={countUnselectedDomainRegistration}
              countDataCheckSet={countDataCheckSet}
            />
          </Elements>
        ) : (
          <CartWizardFinish
            countMissingDataChecks={countUnselectedDataCheck}
            countMissingDomainRegistrations={countUnselectedDomainRegistration}
            countDataCheckSet={countDataCheckSet}
          />
        )}
      </Form>
    </div>
  );
}

const mapStateToProps = state => {
  return {
    user: state.login.user,
    appIntl: state.intl,
  };
};
export default withRouter(legacyInjectIntl(connect(mapStateToProps)(CartWizard)));

function getItemDetails(item, intl) {
  return item.attributes
    ? item.attributes.map(attribute => {
        const { id: key, name, values } = attribute;
        return {
          key,
          label: getNameInCurrentLanguage(name, intl) + ':',
          description:
            Array.isArray(values) && values.length > 0 ? getNameInCurrentLanguage(values[0].value, intl) : undefined,
        };
      })
    : [];
}
